import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatPct } from "@/lib/apiClient";
import {
  ArrowRight,
  RocketLaunch,
  CurrencyBtc,
  Coin,
  ChartLineUp,
  ShieldCheck,
  Vault,
  Sparkle,
  Scales,
} from "@phosphor-icons/react";

export default function Home() {
  const { t } = useTranslation();
  const [tickers, setTickers] = useState([]);
  useEffect(() => {
    api.get("/prices/crypto").then((r) => setTickers(r.data)).catch(() => {});
  }, []);

  const CATEGORIES = [
    {
      slug: "crypto",
      title: t("nav.crypto"),
      icon: CurrencyBtc,
      subtitle: t("home.catCrypto"),
      image:
        "https://images.unsplash.com/photo-1631603090989-93f9ef6f9d80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHxnb2xkJTIwY29pbiUyMGNyeXB0byUyMGRhcmt8ZW58MHx8fHwxNzg3NjE3OTMwfDA&ixlib=rb-4.1.0&q=85",
    },
    {
      slug: "gold",
      title: t("nav.gold"),
      icon: Coin,
      subtitle: t("home.catGold"),
      image:
        "https://images.unsplash.com/photo-1628151015968-3a4429e9ef04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxnb2xkJTIwY29pbiUyMGNyeXB0byUyMGRhcmt8ZW58MHx8fHwxNzg3NjE3OTMwfDA&ixlib=rb-4.1.0&q=85",
    },
    {
      slug: "stocks",
      title: t("nav.stocks"),
      icon: ChartLineUp,
      subtitle: t("home.catStocks"),
      image:
        "https://images.unsplash.com/photo-1642104704074-907c0698cbd9?auto=format&fit=crop&w=1600&q=80",
    },
    {
      slug: "spacex",
      title: t("home.categorySpacexTitle"),
      icon: RocketLaunch,
      subtitle: t("home.catSpacex"),
      image:
        "https://images.pexels.com/photos/23788/pexels-photo.jpg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200",
    },
  ];

  const features = [
    { icon: Vault, h: t("home.f1h"), p: t("home.f1p") },
    { icon: ShieldCheck, h: t("home.f2h"), p: t("home.f2p") },
    { icon: Scales, h: t("home.f3h"), p: t("home.f3p") },
    { icon: Sparkle, h: t("home.f4h"), p: t("home.f4p") },
  ];

  const stats = [
    ["$1.2B", t("home.stat1")],
    ["4", t("home.stat2")],
    ["24/7", t("home.stat3")],
    ["0", t("home.stat4")],
  ];

  return (
    <div data-testid="home-page" className="text-white">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grain" />
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1552925766-63ab07391e02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBkYXJrfGVufDB8fHx8MTc4NzYxNzkzMHww&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-[#050505]" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-24 pb-32 lg:pt-40 lg:pb-44">
          <div className="max-w-3xl fade-up">
            <div className="overline mb-6">{t("home.overline")}</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.02] mb-8">
              {t("home.heroTitle1")} <br />
              {t("home.heroTitle2")}{" "}
              <em className="text-[#E6C998] not-italic">{t("home.heroTitleEm")}</em>{" "}
              {t("home.heroTitle3")}
            </h1>
            <p className="text-neutral-300 text-lg max-w-xl leading-relaxed mb-10">
              {t("home.heroSubtitle")}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/register"
                data-testid="hero-open-account"
                className="btn-gold px-8 py-4 rounded-none text-sm inline-flex items-center gap-2"
              >
                {t("home.openAccount")}
                <ArrowRight size={16} weight="bold" className="rtl-flip" />
              </Link>
              <Link
                to="/marketplace/crypto"
                data-testid="hero-explore"
                className="btn-outline-gold px-8 py-4 rounded-none text-sm"
              >
                {t("home.exploreMarketplace")}
              </Link>
            </div>
          </div>
        </div>

        {/* live ticker */}
        <div className="relative border-y border-white/10 bg-black/50 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center gap-8 overflow-x-auto no-scrollbar">
            <span className="overline whitespace-nowrap">{t("home.liveCrypto")}</span>
            {tickers.length === 0 && (
              <span className="text-xs text-neutral-500">{t("home.fetchingPrices")}</span>
            )}
            {tickers.map((tk) => (
              <div
                key={tk.symbol}
                data-testid={`ticker-${tk.symbol}`}
                className="flex items-center gap-3 whitespace-nowrap"
              >
                <span className="text-neutral-400 text-xs">{tk.symbol}</span>
                <span className="num text-white text-sm">
                  ${tk.price.toLocaleString()}
                </span>
                <span
                  className={`num text-xs ${
                    tk.change_24h >= 0 ? "text-emerald-400" : "text-red-400"
                  }`}
                >
                  {formatPct(tk.change_24h)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ASSET CATEGORIES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <div className="overline mb-3">{t("home.categoriesOverline")}</div>
            <h2 className="font-display text-3xl lg:text-5xl max-w-2xl">
              {t("home.categoriesTitle")}
            </h2>
          </div>
          <p className="text-neutral-400 max-w-md">
            {t("home.categoriesLead")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {CATEGORIES.map((c, idx) => (
            <Link
              to={`/marketplace/${c.slug}`}
              key={c.slug}
              data-testid={`category-card-${c.slug}`}
              className={`lux-card group relative overflow-hidden ${
                idx % 3 === 0 ? "md:col-span-1" : ""
              }`}
            >
              <div className="relative h-64 md:h-80 overflow-hidden">
                <img
                  src={c.image}
                  alt={c.title}
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-[1.03] transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/40 to-transparent" />
                <div className="absolute top-5 left-5">
                  <c.icon
                    size={26}
                    weight="duotone"
                    className="text-[#E6C998]"
                  />
                </div>
                <div className="absolute bottom-5 left-5 right-5">
                  <h3 className="font-display text-2xl mb-1">{c.title}</h3>
                  <p className="text-sm text-neutral-300">{c.subtitle}</p>
                </div>
                <div className="absolute top-5 right-5 text-[#E6C998] opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowRight size={22} weight="bold" className="rtl-flip" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* PLATFORM OVERVIEW */}
      <section className="border-t border-white/10 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="overline mb-3">{t("home.platformOverline")}</div>
            <h2 className="font-display text-3xl lg:text-5xl mb-6">
              {t("home.platformTitle")}
            </h2>
            <p className="text-neutral-400 leading-relaxed mb-10 max-w-lg">
              {t("home.platformLead")}
            </p>
            <ul className="space-y-5">
              {features.map((f) => (
                <li key={f.h} className="flex gap-4">
                  <f.icon
                    size={26}
                    weight="duotone"
                    className="text-[#E6C998] shrink-0"
                  />
                  <div>
                    <div className="text-white font-medium mb-1">{f.h}</div>
                    <div className="text-sm text-neutral-400">{f.p}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative aspect-[4/5] overflow-hidden bg-[#141414] border border-white/10">
            <img
              src="https://images.unsplash.com/photo-1786041176778-14825b13576f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBkYXJrfGVufDB8fHx8MTc4NzYxNzkzMHww&ixlib=rb-4.1.0&q=85"
              alt="Reserved Building"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <div className="overline mb-2">{t("home.reservedMMXXVI")}</div>
              <div className="font-display text-2xl">
                {t("home.cities")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STATS */}
      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map(([n, l]) => (
            <div key={l}>
              <div className="font-display text-4xl text-[#E6C998] mb-2 num">{n}</div>
              <div className="text-sm text-neutral-400 uppercase tracking-wider">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* RISK DISCLOSURE STRIP */}
      <section className="border-t border-white/10 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <div className="overline mb-4">{t("home.riskOverline")}</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <p className="font-display text-2xl lg:text-3xl leading-snug">
              {t("home.riskHeadline")}
            </p>
            <p className="text-neutral-400 text-sm leading-relaxed">
              {t("home.riskBody")}{" "}
              <Link to="/risk" className="text-[#E6C998] gold-underline">
                {t("home.riskLink")}
              </Link>{" "}
              {t("home.riskAfter")}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grain" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32 text-center">
          <div className="overline mb-4">{t("home.ctaOverline")}</div>
          <h2 className="font-display text-4xl lg:text-6xl mb-6">
            {t("home.ctaTitle")}
          </h2>
          <p className="text-neutral-400 max-w-xl mx-auto mb-10">
            {t("home.ctaLead")}
          </p>
          <Link
            to="/register"
            data-testid="cta-open-account"
            className="btn-gold inline-flex items-center gap-2 px-10 py-4 rounded-none text-sm"
          >
            {t("home.openAccount")} <ArrowRight size={16} weight="bold" className="rtl-flip" />
          </Link>
        </div>
      </section>
    </div>
  );
}
